import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SiteShell from "@/components/feature/SiteShell";
import JsonLd from "@/components/feature/JsonLd";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import FilterBar from "@/pages/archive/components/FilterBar";
import type { Article } from "@/types/content";

const decadeOf = (year: string) => {
  if (year.startsWith("199")) return "1990s";
  if (year.startsWith("200")) return "2000s";
  if (year.startsWith("201")) return "2010s";
  return "2020s";
};

const ArchivePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeCat = searchParams.get("cat") || "all";
  const activeEra = searchParams.get("era") || "all";
  const search = searchParams.get("q") || "";

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "all") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return [...articles]
      .filter((item) => (activeCat === "all" ? true : item.category === activeCat))
      .filter((item) => (activeEra === "all" ? true : decadeOf(item.year) === activeEra))
      .filter((item) => {
        if (!keyword) return true;
        return (
          item.title.toLowerCase().includes(keyword) ||
          item.excerpt.toLowerCase().includes(keyword) ||
          item.tags.join(" ").toLowerCase().includes(keyword)
        );
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1)) as Article[];
  }, [activeCat, activeEra, search]);

  const activeCatName =
    activeCat === "all"
      ? "全部主题"
      : categories.find((cat) => cat.id === activeCat)?.name || "全部主题";

  const resetFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  useSeo({
    title: "考古合集 - 热门黑料旧料翻出｜考古坞年代归档",
    description:
      "考古坞考古合集页，把热门黑料历史回顾按主题与年代整理成可筛选的条目库。可搜索选秀记忆、影视旧账、乐坛回响、网络初代等旧料翻出内容，附年份与出处。",
    keywords: "热门黑料考古合集,热门黑料旧料翻出,热门黑料历史回顾,娱乐往事归档",
    image:
      "https://readdy.ai/api/search-image?query=vintage%20empty%20stage%20under%20a%20single%20warm%20spotlight%20with%20a%20retro%20microphone%20stand%2C%20film%20grain%2C%20deep%20brick%20red%20and%20aged%20gold%20tones%2C%20dust%20particles%20in%20the%20light%2C%20artistic%20editorial%20still%2C%20no%20people%2C%20warm%20nostalgic%20mood&width=800&height=600&seq=arch-xuanxiu-2011&orientation=landscape",
  });

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "考古合集 · 热门黑料旧料翻出",
      description:
        "按主题与年代整理的娱乐往事条目库，包含选秀记忆、影视旧账、乐坛回响、网络初代四个分类。",
      inLanguage: "zh-CN",
      url: "https://kaogudock.cn/archive",
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://kaogudock.cn/" },
        { "@type": "ListItem", position: 2, name: "考古合集", item: "https://kaogudock.cn/archive" },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "考古合集条目",
      numberOfItems: filtered.length,
      itemListElement: filtered.slice(0, 10).map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.title,
        url: `https://kaogudock.cn/article/${item.id}`,
      })),
    },
  ];

  return (
    <SiteShell>
      <JsonLd data={structuredData} id="archive-jsonld" />

      <section className="border-b border-foreground-950/10 bg-background-100 paper-grain">
        <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "考古合集" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
            考古合集
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            旧料翻出后统一放进这里，按主题和年代两条线归档。想找某个人的某一年，
            用搜索框直接输关键词会更快。
          </p>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 font-label text-xs text-foreground-500">
            <span>
              当前主题：<span className="text-foreground-900">{activeCatName}</span>
            </span>
            <span>
              年代区间：<span className="text-foreground-900">{activeEra === "all" ? "全部" : activeEra}</span>
            </span>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-shell px-4 py-10 md:px-6 md:py-14">
          <FilterBar
            search={search}
            onSearch={(value) => updateParam("q", value)}
            activeCat={activeCat}
            onCatChange={(value) => updateParam("cat", value)}
            activeEra={activeEra}
            onEraChange={(value) => updateParam("era", value)}
            resultCount={filtered.length}
          />

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="mt-8 rounded-lg border border-dashed border-foreground-950/20 bg-background-100 px-6 py-16 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-inbox-archive-line text-3xl"></i>
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                这一格还没翻到东西
              </h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                换一个关键词，或者把主题和年代放宽一点。也可以直接告诉我们线索，档案室会去找。
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line text-base"></i>
                清空筛选条件
              </button>
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
};

export default ArchivePage;